import React, { useState, useEffect, useContext } from 'react';
import { getTemplateById, getDefaultBranch } from '../templates';
import SmartAddressInput from '../components/SmartAddressInput';
import BulkImportModal from '../components/BulkImportModal';
import { Input } from '../../ui';

// Import standalone context and the JourneyContext directly
import { StandaloneJourneyContext } from '../../StandaloneBranchSetup';
import JourneyContext from '../JourneyContext';

// Hook that tries standalone context first, then journey context
// Both contexts are checked unconditionally to comply with React hooks rules
function useJourneyOrStandalone() {
  const standaloneContext = useContext(StandaloneJourneyContext);
  const journeyContext = useContext(JourneyContext);
  
  // If standalone context exists and has data, use it; otherwise use journey context
  if (standaloneContext) {
    return standaloneContext;
  }
  
  if (!journeyContext) {
    throw new Error('BranchSetupStep must be used within a JourneyProvider or StandaloneBranchSetup');
  }
  
  return journeyContext;
}

// Validation helper
const validateBranch = (branch) => {
  const errors = {
    info: [],
    address: [],
    infra: [],
  };
  
  // Basic Info validation
  if (!branch.name || branch.name.trim() === '') {
    errors.info.push('Branch name is required');
  }

  if (!branch.contactPerson || branch.contactPerson.trim() === '') {
    errors.info.push('Contact Person is required');
  }
  if (!branch.contactNumber || branch.contactNumber.trim() === '') {
    errors.info.push('Contact Number is required');
  }
  const email = (branch.emailAddress || '').trim();
  if (!email) {
    errors.info.push('Email Address is required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
    errors.info.push('Email Address must be valid');
  }
  
  // Address validation - at least city should be filled
  if (!branch.city || branch.city.trim() === '') {
    errors.address.push('City is required');
  }
  
  // Infrastructure validation - at least one value should be set
  const hasInfra = (branch.numberOfUsers > 0) || 
                   (branch.numberOfDevices > 0) || 
                   (branch.numberOfServers > 0) || 
                   (branch.numberOfNetworkDevices > 0) ||
                   branch.hasFirewall;
  if (!hasInfra) {
    errors.infra.push('Please configure at least one infrastructure item');
  }
  
  return errors;
};

const getFirstErrorTab = (errors) => {
  if (errors.info.length > 0) return 'info';
  if (errors.address.length > 0) return 'address';
  if (errors.infra.length > 0) return 'infra';
  return null;
};

// Simple number stepper component
const NumberStepper = ({ label, value, onChange, icon, description }) => {
  const val = value || 0;
  
  return (
    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center text-slate-300">
            {icon}
          </div>
          <div>
            <p className="font-medium text-white">{label}</p>
            {description && <p className="text-xs text-slate-400">{description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onChange(Math.max(0, val - 1))}
            className="w-9 h-9 rounded-lg border border-slate-600 flex items-center justify-center hover:bg-slate-700 transition-colors"
          >
            <span className="text-xl font-medium text-slate-300">−</span>
          </button>
          <input
            type="number"
            min="0"
            value={val}
            onChange={(e) => onChange(Math.max(0, parseInt(e.target.value) || 0))}
            className="w-16 h-9 text-center text-lg font-semibold rounded-lg border border-slate-600 bg-slate-900 text-white"
          />
          <button
            type="button"
            onClick={() => onChange(val + 1)}
            className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors"
          >
            <span className="text-xl font-medium">+</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Branch editor with simple vertical layout
const BranchEditor = ({ branch, onChange, companyAddress, errors, activeSection, setActiveSection }) => {

  const handleChange = (field, value) => {
    onChange({ ...branch, [field]: value });
  };

  const handleAddressSelect = (addressData) => {
    onChange({
      ...branch,
      address: addressData.address || branch.address,
      city: addressData.city || branch.city,
      province: addressData.province || branch.province,
      postalCode: addressData.postalCode || branch.postalCode,
      country: addressData.country || branch.country,
    });
  };

  const copyFromCompany = () => {
    if (companyAddress) {
      onChange({
        ...branch,
        address: companyAddress.address || '',
        city: companyAddress.city || '',
        province: companyAddress.province || '',
        postalCode: companyAddress.postalCode || '',
        country: companyAddress.country || '',
      });
    }
  };

  const sections = [
    { id: 'info', label: 'Basic Info' },
    { id: 'address', label: 'Address' },
    { id: 'infra', label: 'Infrastructure' },
  ];

  const hasErrors = (sectionId) => errors && errors[sectionId] && errors[sectionId].length > 0;

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      {/* Section tabs */}
      <div className="flex border-b border-slate-700">
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveSection(section.id)}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
              activeSection === section.id
                ? 'bg-blue-900/30 text-blue-400 border-b-2 border-blue-500'
                : hasErrors(section.id)
                ? 'text-red-400 bg-red-900/20'
                : 'text-slate-400 hover:bg-slate-700/50'
            }`}
          >
            {section.label}
            {hasErrors(section.id) && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </button>
        ))}
      </div>

      {/* Section content */}
      <div className="p-5 bg-slate-900/30">
        {activeSection === 'info' && (
          <div className="space-y-4">
            {hasErrors('info') && (
              <div className="p-3 bg-red-900/20 border border-red-800 rounded-lg">
                <p className="text-sm text-red-400 font-medium">⚠️ {errors.info.join(', ')}</p>
              </div>
            )}
            <Input
              label="Branch Name"
              required
              value={branch.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Head Office"
              error={hasErrors('info') && !branch.name}
            />
            <Input
              label="Contact Person"
              value={branch.contactPerson || ''}
              onChange={(e) => handleChange('contactPerson', e.target.value)}
              placeholder="John Smith"
            />
            <Input
              label="Contact Number"
              value={branch.contactNumber || ''}
              onChange={(e) => handleChange('contactNumber', e.target.value)}
              placeholder="+27 11 123 4567"
            />
            <Input
              label="Email Address"
              type="email"
              value={branch.emailAddress || ''}
              onChange={(e) => handleChange('emailAddress', e.target.value)}
              placeholder="branch@company.co.za"
            />
          </div>
        )}

        {activeSection === 'address' && (
          <div className="space-y-4">
            {hasErrors('address') && (
              <div className="p-3 bg-red-900/20 border border-red-800 rounded-lg">
                <p className="text-sm text-red-400 font-medium">⚠️ {errors.address.join(', ')}</p>
              </div>
            )}
            {companyAddress?.address && (
              <button
                type="button"
                onClick={copyFromCompany}
                className="w-full p-3 rounded-lg border-2 border-dashed border-blue-500/40 bg-blue-900/20 text-blue-400 hover:bg-blue-900/30 transition-colors text-sm font-medium"
              >
                📋 Copy Address from Company
              </button>
            )}
            <SmartAddressInput
              value={branch.address || ''}
              onChange={(value) => handleChange('address', value)}
              onSelect={handleAddressSelect}
            />
            <Input
              label="City"
              value={branch.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Johannesburg"
            />
            <Input
              label="Province"
              value={branch.province || ''}
              onChange={(e) => handleChange('province', e.target.value)}
              placeholder="Gauteng"
            />
            <Input
              label="Postal Code"
              value={branch.postalCode || ''}
              onChange={(e) => handleChange('postalCode', e.target.value)}
              placeholder="2000"
            />
            <Input
              label="Country"
              value={branch.country || ''}
              onChange={(e) => handleChange('country', e.target.value)}
              placeholder="South Africa"
            />
          </div>
        )}

        {activeSection === 'infra' && (
          <div className="space-y-3">
            {hasErrors('infra') && (
              <div className="p-3 bg-red-900/20 border border-red-800 rounded-lg">
                <p className="text-sm text-red-400 font-medium">⚠️ {errors.infra.join(', ')}</p>
              </div>
            )}
            {/* Firewall toggle */}
            <div 
              onClick={() => handleChange('hasFirewall', !branch.hasFirewall)}
              className={`
                rounded-xl p-4 flex items-center justify-between cursor-pointer transition-all
                ${branch.hasFirewall 
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-500' 
                  : 'bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700'
                }
              `}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  branch.hasFirewall ? 'bg-emerald-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                }`}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Firewall</p>
                  <p className="text-xs text-gray-500">Network security</p>
                </div>
              </div>
              <div className={`w-12 h-7 rounded-full relative ${branch.hasFirewall ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${branch.hasFirewall ? 'left-6' : 'left-1'}`} />
              </div>
            </div>

            {/* Number steppers */}
            <NumberStepper
              label="Users"
              description="Number of user accounts"
              value={branch.numberOfUsers}
              onChange={(v) => handleChange('numberOfUsers', v)}
              icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>}
            />
            <NumberStepper
              label="Devices"
              description="Computers, laptops, etc."
              value={branch.numberOfDevices}
              onChange={(v) => handleChange('numberOfDevices', v)}
              icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
            />
            <NumberStepper
              label="Servers"
              description="Physical or virtual"
              value={branch.numberOfServers}
              onChange={(v) => handleChange('numberOfServers', v)}
              icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" /></svg>}
            />
            <NumberStepper
              label="Network Devices"
              description="Routers, switches, APs"
              value={branch.numberOfNetworkDevices}
              onChange={(v) => handleChange('numberOfNetworkDevices', v)}
              icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" /></svg>}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default function BranchSetupStep({ onValidate }) {
  const { branches, company, addBranch, updateBranch, removeBranch, selectedTemplate, updateBranches } = useJourneyOrStandalone();
  const [activeBranchIndex, setActiveBranchIndex] = useState(0);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [activeSection, setActiveSection] = useState('info');
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);

  const template = selectedTemplate ? getTemplateById(selectedTemplate) : null;

  // Validate all branches and return true if valid
  const validateAllBranches = () => {
    if (branches.length === 0) {
      return { valid: false, message: 'Please add at least one branch' };
    }

    const allErrors = {};
    let firstErrorBranch = -1;
    let firstErrorTab = null;

    branches.forEach((branch, index) => {
      const errors = validateBranch(branch);
      const hasError = errors.info.length > 0 || errors.address.length > 0 || errors.infra.length > 0;
      
      if (hasError) {
        allErrors[index] = errors;
        if (firstErrorBranch === -1) {
          firstErrorBranch = index;
          firstErrorTab = getFirstErrorTab(errors);
        }
      }
    });

    setValidationErrors(allErrors);
    setShowValidation(true);

    if (Object.keys(allErrors).length > 0) {
      // Navigate to first branch with error
      setActiveBranchIndex(firstErrorBranch);
      if (firstErrorTab) {
        setActiveSection(firstErrorTab);
      }
      return { valid: false, message: 'Please complete all branch information' };
    }

    return { valid: true };
  };

  // Expose validation to parent via callback
  useEffect(() => {
    if (onValidate) {
      onValidate(validateAllBranches);
    }
  }, [branches, onValidate]);

  // Clear validation errors when branch data changes
  useEffect(() => {
    if (showValidation && branches[activeBranchIndex]) {
      const errors = validateBranch(branches[activeBranchIndex]);
      setValidationErrors(prev => ({
        ...prev,
        [activeBranchIndex]: errors
      }));
    }
  }, [branches, activeBranchIndex, showValidation]);

  const handleAddBranch = () => {
    const newBranch = getDefaultBranch(template);
    addBranch(newBranch);
    setActiveBranchIndex(branches.length);
  };

  const handleBranchChange = (updatedBranch) => {
    updateBranch(activeBranchIndex, updatedBranch);
  };

  const handleRemoveBranch = (index) => {
    removeBranch(index);
    if (activeBranchIndex >= branches.length - 1) {
      setActiveBranchIndex(Math.max(0, branches.length - 2));
    }
  };

  const handleBulkImport = (importedBranches) => {
    const newBranches = [...branches, ...importedBranches];
    updateBranches(newBranches);
    setActiveBranchIndex(branches.length);
    setShowBulkImport(false);
  };

  const activeBranch = branches[activeBranchIndex];

  return (
    <div>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Branch Setup</h2>
            <p className="text-sm text-slate-400">Configure branches for {company.name || 'your company'}</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowBulkImport(true)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Import CSV
            </button>
            <button
              type="button"
              onClick={handleAddBranch}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              + Add Branch
            </button>
          </div>
        </div>

        {branches.length === 0 ? (
          /* Empty state */
          <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-xl bg-slate-800/50">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-900/50 flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No branches yet</h3>
            <p className="text-slate-400 mb-4">Add your first branch to get started</p>
            <button
              onClick={handleAddBranch}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              + Add First Branch
            </button>
          </div>
        ) : (
          /* Branch list and editor */
          <div className="space-y-4">
            {/* Branch selector */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {branches.map((branch, index) => {
                const branchHasErrors = showValidation && validationErrors[index] && 
                  (validationErrors[index].info?.length > 0 || 
                   validationErrors[index].address?.length > 0 || 
                   validationErrors[index].infra?.length > 0);
                
                return (
                  <button
                    key={branch.id || index}
                    type="button"
                    onClick={() => { setActiveBranchIndex(index); setActiveSection('info'); }}
                    className={`
                      flex-shrink-0 px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 relative
                      ${index === activeBranchIndex
                        ? branchHasErrors ? 'bg-red-500 text-white' : 'bg-blue-600 text-white'
                        : branchHasErrors 
                          ? 'bg-red-900/30 text-red-300 border border-red-700'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }
                    `}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                      branchHasErrors ? 'bg-white/30' : 'bg-white/20'
                    }`}>
                      {branchHasErrors ? '!' : index + 1}
                    </span>
                    <span className="max-w-[120px] truncate">{branch.name || `Branch ${index + 1}`}</span>
                    {index === activeBranchIndex && branches.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemoveBranch(index); }}
                        className="ml-1 w-5 h-5 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                      >
                        ×
                      </button>
                    )}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={handleAddBranch}
                className="flex-shrink-0 px-4 py-2 rounded-lg border-2 border-dashed border-slate-600 text-slate-400 hover:border-blue-400 hover:text-blue-400 transition-colors text-sm"
              >
                + Add
              </button>
            </div>

            {/* Quick stats bar */}
            {activeBranch && (
              <div className="flex flex-wrap gap-2 py-2">
                {activeBranch.hasFirewall && (
                  <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded text-xs font-medium">🛡️ Firewall</span>
                )}
                {(activeBranch.numberOfUsers || 0) > 0 && (
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">👥 {activeBranch.numberOfUsers} Users</span>
                )}
                {(activeBranch.numberOfDevices || 0) > 0 && (
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs font-medium">💻 {activeBranch.numberOfDevices} Devices</span>
                )}
                {(activeBranch.numberOfServers || 0) > 0 && (
                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded text-xs font-medium">🖥️ {activeBranch.numberOfServers} Servers</span>
                )}
                {(activeBranch.numberOfNetworkDevices || 0) > 0 && (
                  <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded text-xs font-medium">📡 {activeBranch.numberOfNetworkDevices} Network</span>
                )}
              </div>
            )}

            {/* Branch editor */}
            {activeBranch && (
              <BranchEditor
                branch={activeBranch}
                onChange={handleBranchChange}
                companyAddress={{
                  address: company.address,
                  city: company.city,
                  province: company.province,
                  postalCode: company.postalCode,
                  country: company.country,
                }}
                errors={showValidation ? validationErrors[activeBranchIndex] : null}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
              />
            )}
          </div>
        )}
      </div>

      {/* Bulk Import Modal */}
      <BulkImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImport={handleBulkImport}
      />
    </div>
  );
}

